import { Uploader, UploadQueueEvent } from '@wix/media-manager-sdk';
import React, { createContext, FC, useEffect } from 'react';
import { Subject } from 'rxjs';
import { DuplexerEvent } from '../../../constants/DuplexerEvent';
import { TranscodingStatus } from '../../../constants/TranscodingStatus';
import { EVENT_STORAGE_QUOTA_SERVER_ERROR, useAppEvents } from '../../../services/appEvents';
import { UploadError } from '../../../constants/UploadError';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import {
  MediaManagerSDKFolder,
  MediaManagerSDKTotalProgress,
  MediaManagerSDKUploadingFile,
  MediaManagerSDKUploadingFileError,
} from '../../media-manager-sdk';
import { isStatusOkayToDisplayInItemsList } from '../../media-manager-sdk/transcoding';
import { getErrorInfo, isFileFailed, isFileUploaded } from '../../uploadingFile';
import { IDuplexerMessage, useDuplexer } from '../useDuplexer';
import { useBooleanRef } from '../useBoolean';
import { useUploadQueueReducer } from './reducer';
import { IFilesUploaderContext, IImportOperation, IOuter } from './types';

const queueStartObservable = new Subject<MediaManagerSDKUploadingFile[]>();
const queueChangeObservable = new Subject<MediaManagerSDKUploadingFile[]>();
const queueFinishedObservable = new Subject<MediaManagerSDKUploadingFile[]>();
const itemUploadedObservable = new Subject<MediaManagerSDKUploadingFile>();
const folderUploadedObservable = new Subject<MediaManagerSDKFolder>();

const importOperations = new Map<string, IImportOperation>();

export const FilesUploaderContext = createContext<IFilesUploaderContext>({
  queueStartObservable,
  queueChangeObservable,
  queueFinishedObservable,
  itemUploadedObservable,
  folderUploadedObservable,

  importOperations,

  cancelAllUploads: () => {},
  clearUploadingQueue: () => {},
  removeFileFromUploadingQueue: () => {},

  uploadingFiles: [],
  uploadedFiles: [],
  invalidFiles: [],
  failedFiles: [],

  filesCount: 0,
  notFailedFilesCount: 0,
  notAbortedFilesCount: 0,
  uploadedFilesCount: 0,
  invalidFilesCount: 0,
  failedFilesCount: 0,

  hasFailedFiles: false,
  hasAbortableFiles: false,
  hasUploadingFiles: false,
  hasNetworkErrorResumableFiles: false,
  hasServerErrorAutomaticResumeFile: false,
  areAllFilesUploaded: false,
});

export const FilesUploaderProvider: FC<IOuter> = ({ uploadingFiles: uploadingFilesFromProps = [], children }) => {
  const { mmgrSettings } = useMMGRSettings();
  const { maxParallelUploads } = mmgrSettings;

  const {
    uploadingFiles,
    setUploadQueue,
    updateUploadingProgress,
    clearQueue,
    filesCount,
    hasUploadingFiles,
    hasNetworkErrorResumableFiles,
    hasServerErrorAutomaticResumeFile,
    notAbortedFilesCount,
    uploadedFiles,
    invalidFiles,
    failedFiles,
    hasFailedFiles,
    hasAbortableFiles,
    invalidFilesCount,
    uploadedFilesCount,
    failedFilesCount,
    notFailedFilesCount,
    areAllFilesUploaded,
  } = useUploadQueueReducer(uploadingFilesFromProps);

  const { appEvents } = useAppEvents();

  const {
    get: shouldUpdateFileStatus,
    setTrue: resumeFileStatusUpdate,
    setFalse: preventFileStatusUpdate,
  } = useBooleanRef(false);

  const cancelAllUploads = () => {
    preventFileStatusUpdate();
    Uploader.cancelAllUploads();
  };

  const clearUploadingQueue = () => {
    Uploader.clearQueue();
    clearQueue();
  };

  const removeFileFromUploadingQueue = (uploadingFile) => {
    Uploader.removeItemFromQueue(uploadingFile);
  };

  const handleAsyncImportStatusUpdate = ({ file, error: errorInfo }) => {
    const { fileName, opStatus } = file;
    const operation = importOperations.get(fileName);
    if (!operation) {
      return;
    }

    if (isStatusOkayToDisplayInItemsList(opStatus)) {
      const updatedItemData = { transcodingStatus: opStatus, fileUrl: fileName };

      if (opStatus === TranscodingStatus.Failed) {
        /* The backend doesn't send us full errors, just the .info field. */
        const message = errorInfo.message;
        const code = -1;
        const parsedError: MediaManagerSDKUploadingFileError = {
          code,
          message,
          config: null,
          data: {
            error_code: code,
            error_description: message,
            error_info: errorInfo,
          },
        };
        operation.onFail({ ...updatedItemData, error: parsedError });
      } else {
        operation.onDone(updatedItemData);
      }
      importOperations.delete(fileName);
    }
  };

  const handleCopyDone = (data: IDuplexerMessage) => {
    const { opId } = data.info;

    const operation = importOperations.get(opId);
    if (!operation) {
      return;
    }

    operation.onDone(data.info);
    importOperations.delete(opId);
  };

  useDuplexer({
    [DuplexerEvent.FileStatusUpdate]: handleAsyncImportStatusUpdate,
    [DuplexerEvent.CopyFile]: handleCopyDone,
    [DuplexerEvent.CopyFolder]: handleCopyDone,
  });

  useEffect(() => {
    Uploader.maxParallelUploads = maxParallelUploads;
  }, [maxParallelUploads]);

  useEffect(() => {
    const handleUploadQueueStarted = (queue) => {
      resumeFileStatusUpdate();
      queueStartObservable.next(queue);
    };
    const handleUploadQueueChanged = (queue) => queueChangeObservable.next(queue);
    const handleUploadQueueFinished = (queue) => queueFinishedObservable.next(queue);

    const handleUploadFileStatusChange = (progress: MediaManagerSDKTotalProgress) => {
      if (shouldUpdateFileStatus()) {
        updateUploadingProgress(progress);
      }

      const file = progress.currentFileProgress.file;

      if (isFileFailed(file) && getErrorInfo(file.error).key === UploadError.QuotaTotalStorageExceeded) {
        appEvents.emit(EVENT_STORAGE_QUOTA_SERVER_ERROR);
      }

      if (isFileUploaded(file)) {
        itemUploadedObservable.next(file);
      }
    };

    Uploader.on(UploadQueueEvent.UploadQueueStarted, setUploadQueue);
    Uploader.on(UploadQueueEvent.UploadQueueStarted, handleUploadQueueStarted);

    Uploader.on(UploadQueueEvent.QueueChanged, setUploadQueue);
    Uploader.on(UploadQueueEvent.QueueChanged, handleUploadQueueChanged);

    Uploader.on(UploadQueueEvent.FileStatusChanged, handleUploadFileStatusChange);

    Uploader.on(UploadQueueEvent.UploadQueueFinished, setUploadQueue);
    Uploader.on(UploadQueueEvent.UploadQueueFinished, handleUploadQueueFinished);

    return () => {
      Uploader.off(UploadQueueEvent.UploadQueueStarted, setUploadQueue);
      Uploader.off(UploadQueueEvent.UploadQueueStarted, handleUploadQueueStarted);

      Uploader.off(UploadQueueEvent.QueueChanged, setUploadQueue);
      Uploader.off(UploadQueueEvent.QueueChanged, handleUploadQueueChanged);

      Uploader.off(UploadQueueEvent.FileStatusChanged, handleUploadFileStatusChange);

      Uploader.off(UploadQueueEvent.UploadQueueFinished, setUploadQueue);
      Uploader.off(UploadQueueEvent.UploadQueueFinished, handleUploadQueueFinished);
    };
  }, []);

  return (
    <FilesUploaderContext.Provider
      value={{
        queueStartObservable,
        queueChangeObservable,
        queueFinishedObservable,
        itemUploadedObservable,
        folderUploadedObservable,

        importOperations,

        cancelAllUploads,
        clearUploadingQueue,
        removeFileFromUploadingQueue,

        uploadingFiles,
        filesCount,
        hasUploadingFiles,
        notAbortedFilesCount,
        uploadedFiles,
        invalidFiles,
        failedFiles,
        hasFailedFiles,
        hasAbortableFiles,
        hasNetworkErrorResumableFiles,
        hasServerErrorAutomaticResumeFile,
        uploadedFilesCount,
        invalidFilesCount,
        failedFilesCount,
        notFailedFilesCount,
        areAllFilesUploaded,
      }}
    >
      {children}
    </FilesUploaderContext.Provider>
  );
};
